import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Filters the online-store cards by the product-type category chips.
 * All card/category data is server-rendered, so filtering is a pure
 * show/hide toggle with no fetching involved. On phones the grid becomes
 * a one-card swipeable carousel with dot pagination mirroring the store
 * locator's mobile list.
 *
 * @typedef {{ grid: HTMLElement, dots: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class OnlineStores extends Component {
  requiredRefs = ['grid', 'dots'];

  /** @type {Set<string>} */
  #activeCategoryIds = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.refs.grid.addEventListener('scroll', this.#onGridScroll);
    this.#applyFilters();
  }

  /**
   * @param {{id: string}} data
   * @param {Event} event
   */
  toggleCategory(data, event) {
    const chip = /** @type {HTMLButtonElement} */ (event.target);
    const id = data.id;
    const isActive = this.#activeCategoryIds.has(id);

    chip.classList.toggle('is-active', !isActive);
    chip.setAttribute('aria-pressed', String(!isActive));

    if (isActive) {
      this.#activeCategoryIds.delete(id);
    } else {
      this.#activeCategoryIds.add(id);
    }

    this.#applyFilters();
  }

  #applyFilters() {
    const activeCategoryIds = this.#activeCategoryIds;

    // Clones from a previous render must go before recalculating visibility,
    // otherwise they'd be counted as extra (mismatched) cards.
    for (const clone of this.querySelectorAll('.online-stores__card[data-clone]')) {
      clone.remove();
    }

    for (const card of this.querySelectorAll('.online-stores__card')) {
      const categoryIds = (card.getAttribute('data-category-ids') || '').split(' ').filter(Boolean);
      const matches = !activeCategoryIds.size || categoryIds.some((id) => activeCategoryIds.has(id));

      if (card instanceof HTMLElement) card.hidden = !matches;
    }

    const visibleCards = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.online-stores__card:not([hidden])'))
    );

    this.#setupLoop(visibleCards);
    this.#renderDots(visibleCards);
  }

  /**
   * Loops the mobile carousel the same way the store locator's list does:
   * a clone of the last visible card before the first, a clone of the first
   * after the last, starting the scroll position on the first real card.
   *
   * @param {HTMLElement[]} visibleCards
   */
  #setupLoop(visibleCards) {
    const { grid } = this.refs;

    if (visibleCards.length <= 1) {
      grid.scrollLeft = 0;
      return;
    }

    const leadingClone = /** @type {HTMLElement} */ (visibleCards[visibleCards.length - 1].cloneNode(true));
    const trailingClone = /** @type {HTMLElement} */ (visibleCards[0].cloneNode(true));

    for (const clone of [leadingClone, trailingClone]) {
      clone.setAttribute('data-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.tabIndex = -1;
    }

    grid.prepend(leadingClone);
    grid.appendChild(trailingClone);
    grid.scrollLeft = grid.clientWidth;
  }

  /** @param {HTMLElement[]} visibleCards */
  #renderDots(visibleCards) {
    this.refs.dots.innerHTML = '';

    if (visibleCards.length <= 1) return;

    const fragment = document.createDocumentFragment();

    visibleCards.forEach((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `online-stores__dot${index === 0 ? ' is-active' : ''}`;
      dot.setAttribute('aria-label', card.querySelector('.online-stores__card-title')?.textContent?.trim() || '');
      dot.addEventListener('click', () => {
        this.refs.grid.scrollTo({ left: (index + 1) * this.refs.grid.clientWidth, behavior: 'smooth' });
      });
      fragment.appendChild(dot);
    });

    this.refs.dots.appendChild(fragment);
  }

  #onGridScroll = debounce(() => {
    const { grid, dots } = this.refs;
    const realCount = dots.children.length;
    if (!realCount || !grid.clientWidth) return;

    let domIndex = Math.round(grid.scrollLeft / grid.clientWidth);

    if (domIndex <= 0) {
      domIndex = realCount;
      grid.scrollLeft = grid.clientWidth * domIndex;
    } else if (domIndex >= realCount + 1) {
      domIndex = 1;
      grid.scrollLeft = grid.clientWidth * domIndex;
    }

    const activeIndex = domIndex - 1;

    for (const [dotIndex, dot] of Array.from(dots.children).entries()) {
      dot.classList.toggle('is-active', dotIndex === activeIndex);
    }
  }, 100);
}

if (!customElements.get('online-stores')) {
  customElements.define('online-stores', OnlineStores);
}
